import { useEffect } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { useLanguage } from '../../context/LanguageContext';
import { Card, CardContent } from '../../components/ui/card';
import { Cross, Phone, Mail, ExternalLink, AlertCircle, CheckCircle, Info } from 'lucide-react';
import { Button } from '../../components/ui/button';
import usePageBanner from '../../hooks/usePageBanner';
import { AutoTranslate } from '../../hooks/useAutoTranslate';

const BegravningPage = () => {
  const { language, isRTL } = useLanguage();
  const { bannerImage, bannerTitle, bannerSubtitle } = usePageBanner('begravning', language);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const funeralSteps = [
    {
      icon: Phone,
      title: 'Kontakta begravningsbyrå',
      description: 'Katolska kyrkan har inga egna begravningsbyråer. Kontakta en begravningsbyrå så snart som möjligt och berätta att ni önskar en katolsk begravning. Fråga vänner och familj om rekommendationer.',
    },
    {
      icon: Cross,
      title: 'Kontakta prästen',
      description: 'Om den avlidne tillhörde och var aktiv i en viss församling är det lämpligt att kontakta den präst man önskar ska förrätta jordfästningen. Prästen kan hjälpa er att planera ceremonin.',
    },
    {
      icon: CheckCircle,
      title: 'Planera ceremonin',
      description: 'Tillsammans med prästen väljer ni psalmer, bibeltexter och andra detaljer för begravningsmässan. Den katolska begravningen är en bön för den avlidnes själ.',
    },
  ];

  const importantInfo = [
    {
      title: 'Oregistrerade katoliker',
      description: 'Även katoliker som inte är registrerade i en församling kan få katolsk begravning. Kontakta närmaste katolska församling för mer information.',
      type: 'info',
    },
    {
      title: 'Kremering är tillåten',
      description: 'Kremering är tillåten enligt katolska kyrkans regler, men askan måste läggas till vila på en helig plats (kyrkogård). Askan får inte spridas eller förvaras hemma.',
      type: 'info',
    },
    {
      title: 'Utträde ur kyrkan',
      description: 'De som själva har valt att formellt träda ur den katolska kyrkan ska enligt kyrkans ordning inte begravas katolskt.',
      type: 'warning',
    },
  ];

  return (
    <div className="min-h-screen themed-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar />

      {/* Header */}
      <section className="themed-hero relative h-80 overflow-hidden">
        <img
          src={bannerImage || "https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?w=1920"}
          alt="Begravning"
          className="w-full h-full object-cover"
        />
        <div className={`absolute inset-0 bg-gradient-to-r ${isRTL ? 'from-black/40 to-black/70' : 'from-black/70 to-black/40'} flex items-center`}>
          <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full ${isRTL ? 'text-right' : ''}`}>
            <h1 className="responsive-hero-title font-serif font-bold text-white mb-4 fade-in">
              {bannerTitle || <AutoTranslate>Begravning</AutoTranslate>}
            </h1>
            <p className="text-xl text-gray-200 fade-in">
              {bannerSubtitle || <AutoTranslate>Katolsk begravning och jordfästning</AutoTranslate>}
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-xl card-hover mb-8">
            <CardContent className="p-8">
              <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Cross className="w-6 h-6 text-gray-700" />
                </div>
                <div>
                  <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">
                    <AutoTranslate>När en katolik har avlidit</AutoTranslate>
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    <AutoTranslate>
                      Den katolska begravningen är en bön för den avlidnes själ och en tröst för de efterlevande. 
                      Ceremonin uttrycker vår tro på uppståndelsen och det eviga livet. Kontakta en begravningsbyrå 
                      så snart som möjligt efter dödsfallet och berätta att ni önskar en katolsk begravning.
                    </AutoTranslate>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Steps */}
          <h3 className="text-2xl font-serif font-bold text-gray-900 mb-6">
            <AutoTranslate>Steg för att arrangera en katolsk begravning</AutoTranslate>
          </h3>
          
          <div className="space-y-4 mb-12">
            {funeralSteps.map((step, index) => (
              <Card key={`funeral-step-${step.title || index}`} className="border-0 shadow-md hover:shadow-xl card-hover transition-shadow">
                <CardContent className="p-6">
                  <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-gray-700 font-bold">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">
                        <AutoTranslate>{step.title}</AutoTranslate>
                      </h4>
                      <p className="text-gray-600">
                        <AutoTranslate>{step.description}</AutoTranslate>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Important Information */}
          <h3 className="text-2xl font-serif font-bold text-gray-900 mb-6">
            <AutoTranslate>Viktig information</AutoTranslate>
          </h3>
          
          <div className="space-y-4 mb-12">
            {importantInfo.map((item, index) => (
              <Card 
                key={`funeral-info-${item.type || ''}-${index}`} 
                className={`border-0 shadow-md ${item.type === 'warning' ? 'border-l-4 border-amber-500' : 'border-l-4 border-blue-500'}`}
              >
                <CardContent className="p-6">
                  <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${item.type === 'warning' ? 'bg-amber-100' : 'bg-blue-100'}`}>
                      {item.type === 'warning' ? (
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                      ) : (
                        <Info className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">
                        <AutoTranslate>{item.title}</AutoTranslate>
                      </h4>
                      <p className="text-gray-600">
                        <AutoTranslate>{item.description}</AutoTranslate>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Contact Section */}
          <Card className="border-0 shadow-xl card-hover bg-gradient-to-r from-gray-700 to-slate-800 text-white">
            <CardContent className="p-8">
              <h3 className="text-2xl font-serif font-bold mb-4">
                <AutoTranslate>Kontakta oss</AutoTranslate>
              </h3>
              <p className="text-gray-200 mb-6">
                <AutoTranslate>
                  Har du frågor om katolsk begravning? Kontakta vår församling så hjälper vi dig.
                </AutoTranslate>
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="/contact">
                  <Button variant="secondary" className="bg-white text-gray-800 hover:bg-gray-100">
                    <Mail className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    <AutoTranslate>Kontakta församlingen</AutoTranslate>
                  </Button>
                </a>
                <a 
                  href="https://www.katolskakyrkan.se/forsamlingsliv/begravning" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="border-white text-white hover:bg-white/10">
                    <ExternalLink className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    <AutoTranslate>Läs mer på katolskakyrkan.se</AutoTranslate>
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BegravningPage;
